/**
 * Final-rounds endpoints for the TA board.
 *
 * Split from `taActionBoardApi.ts` on a domain line — the final-round cascade (config, chain
 * solving, the booking cascade) is its own subsystem with its own base path — and because that file
 * had grown past the 500-line lint ceiling.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import { tracked } from './taActionBoardAnalytics';
import type {
  BookFinalRoundRequest,
  BookFinalRoundResponse,
  ChainOption,
  FinalRoundConfig,
  SlotHold,
} from '../types/taActionBoard';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

const BASE = `${PROXY_URL}/api/ta-action-board`;

const FR_BASE = `${BASE}/final-rounds`;

/** Throws with the proxy's own error message so toasts stay actionable. */
async function jsonOrThrow<T>(res: Response): Promise<T> {
  const data = (await res.json()) as T & { error?: string };
  if (data?.error) throw new Error(data.error);
  return data;
}

/** Everything the round-template modal can set. */
export type FinalRoundConfigInput = Pick<
  FinalRoundConfig,
  | 'finalInterviewer'
  | 'barRaiser'
  | 'interviewMinutes'
  | 'barRaiserMinutes'
  | 'g2k'
  | 'g2kMinutes'
  | 'hiringManager'
  | 'finalFeedbackTemplate'
  | 'barRaiserFeedbackTemplate'
  | 'recruiterCheckout'
  | 'recruiterCheckoutEmail'
  | 'recruiterCheckoutFeedbackTemplate'
>;

export async function saveFinalRoundConfig(
  postingId: string,
  config: FinalRoundConfigInput
): Promise<{ ok: boolean; config: FinalRoundConfig }> {
  return tracked('ta_board_fr_config', async () => {
    const res = await authenticatedFetch(`${FR_BASE}/config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ postingId, ...config }),
    });
    return jsonOrThrow(res);
  });
}

/**
 * Re-solve chain options after a template edit (the board also delivers them
 * inline). boardEmail matters: the board owner is the default onsite contact and
 * has to be free when the candidate arrives.
 */
export async function fetchFinalRoundChains(
  postingId: string,
  boardEmail?: string
): Promise<{
  config: FinalRoundConfig | null;
  chains: ChainOption[];
  moreChains?: ChainOption[];
  error: string | null;
  /** Why an empty window is empty, when the server can name a reason */
  diagnosis?: string | null;
  lookaheadDays?: number;
}> {
  const qs = new URLSearchParams({ postingId, ...(boardEmail ? { boardEmail } : {}) });
  // "Re-check" means re-read the calendars NOW — same reasoning as the case
  // panel's re-check: a cached answer makes the button look dead.
  const res = await authenticatedFetch(`${FR_BASE}/chains?${qs.toString()}`, { cache: 'no-store' });
  return res.json() as Promise<{
    config: FinalRoundConfig | null;
    chains: ChainOption[];
    moreChains?: ChainOption[];
    error: string | null;
    diagnosis?: string | null;
    lookaheadDays?: number;
  }>;
}

/**
 * The booking cascade for a confirmed chain: per-participant events, room
 * block when onsite, Meet links, debrief, hold release, coordination Slack
 * message — and the candidate summary draft in the response.
 */
export async function bookFinalRound(payload: BookFinalRoundRequest): Promise<BookFinalRoundResponse> {
  return tracked('ta_board_fr_book', async () => {
    const res = await authenticatedFetch(`${FR_BASE}/book`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return jsonOrThrow(res);
  });
}

// ───────────────────────────── CANDIDATE REQUESTS ─────────────────────────────

/** One 15-minute debrief option, graded like the chain options. */
export interface DebriefOption {
  start: string;
  end: string;
  grade: 'free' | 'needs-move';
  conflicts: { email: string; title: string }[];
}

/**
 * The debrief slots to choose from for a chain about to be booked.
 *
 * A POST: it reads the participants' calendars for the window after that chain.
 */
export async function fetchDebriefOptions(payload: {
  postingId: string;
  boardEmail: string;
  chainEnd: string;
  /** 15 (default), 30 or 45 — options are graded for this length. */
  minutes?: number;
}): Promise<{ options: DebriefOption[]; participants: string[]; minutes: number; error?: string }> {
  const res = await authenticatedFetch(`${FR_BASE}/debrief-options`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json();
}

/**
 * Ask the hiring team whether a chain option works, BEFORE offering it to the
 * candidate. DMs to `recipients`, or one post to `channel` — never both.
 *
 * The message text is composed on the proxy from its own solved chains; the
 * recruiter picks which options to ask about and who to ask.
 */
/**
 * The panel ask as it WOULD go out. Sends nothing.
 *
 * The options are resolved from the server's own freshly solved chains, so a
 * preview can legitimately come back with fewer than were ticked — `missing`
 * names them rather than dropping them in silence.
 */
export async function previewFinalRoundAsk(payload: {
  postingId: string;
  opportunityId?: string;
  boardEmail: string;
  starts: string[];
  /** Omitted until the recruiter picks one, so the role's tags decide. */
  language?: 'de' | 'en';
}): Promise<{ text?: string; language?: 'de' | 'en'; missing?: string[]; error?: string }> {
  const res = await authenticatedFetch(`${FR_BASE}/ask-panel/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    // A 400 here is an EXPECTED state, not a failure: the options are re-solved
    // against fresh calendars, so a ticked one can legitimately be gone. Without
    // this, `authenticatedFetch` throws and raises a global error instead of
    // letting the modal say which option went and what to do about it.
    suppressErrorStatuses: [400],
  });
  return res.json();
}

export async function askFinalRoundPanel(payload: {
  postingId: string;
  opportunityId?: string;
  boardEmail: string;
  starts: string[];
  recipients?: string[];
  channel?: string;
  /**
   * Which language the panel reads. Omitted until the recruiter picks one, so
   * the proxy derives it from the role's tags.
   */
  language?: 'de' | 'en';
  /** The recruiter's edit. Blank falls back to the proxy's own composition. */
  text?: string;
}): Promise<{
  ok?: boolean;
  sent?: string[];
  failed?: string[];
  /** Ticked options a fresh calendar read no longer offers — reported, never dropped in silence. */
  missing?: string[];
  /** The edit named an option this send re-solved away. Nothing was sent. */
  staleOptions?: boolean;
  preview?: string;
  error?: string;
}> {
  const res = await authenticatedFetch(`${FR_BASE}/ask-panel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    // A 409 means an edited message named an option the fresh solve removed.
    // Nothing was sent, and the modal re-previews — an expected state, not a
    // failure, so it must not raise a global error.
    suppressErrorStatuses: [409],
  });
  return res.json();
}

// ───────────────────────────── FINAL-ROUND HOLDS ─────────────────────────────

/** The final-round holds for the given roles, keyed by postingId. */
export async function fetchFinalHolds(postingIds: string[]): Promise<Record<string, SlotHold[]>> {
  const ids = postingIds.filter(Boolean).slice(0, 50);
  if (ids.length === 0) return {};
  const res = await authenticatedFetch(
    `${FR_BASE}/holds?postingIds=${encodeURIComponent(ids.join(','))}`,
    { cache: 'no-store' }
  );
  const data = (await res.json()) as { holds?: Record<string, SlotHold[]> };
  return data.holds || {};
}

/**
 * Block the selected chains — one tentative hold per chain window. Only the
 * chain STARTS are sent; the server derives the window and attendees from its
 * own solved options, so a client cannot hold arbitrary people or times.
 */
export async function holdFinalChains(payload: {
  postingId: string;
  starts: string[];
  boardEmail?: string;
}): Promise<{ holds: SlotHold[]; created: number }> {
  const res = await authenticatedFetch(`${FR_BASE}/holds`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res);
}

/** Delete the calendar holds on the named chain starts. */
export async function releaseFinalHold(payload: {
  postingId: string;
  starts: string[];
}): Promise<{ holds: SlotHold[]; released: number }> {
  const res = await authenticatedFetch(`${FR_BASE}/holds/release`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res);
}
